// Pure helpers that turn a course's modules + the user's progress rows into
// what the course card shows: stats, per-module completion, overall progress
// and the lesson the main button should open. No Supabase calls here.

// The modules a learner sees: only published lessons, no module without one,
// numbered 1..n in order (displayIndex) — order_index can have gaps once
// lessons or whole modules are hidden. Admins read unpublished lessons through
// RLS, so the published filter keeps their view the same as everyone else's.
export function visibleModules(modules) {
  return [...(modules || [])]
    .sort((a, b) => a.order_index - b.order_index)
    .map(m => ({
      ...m,
      lessons: [...(m.lessons || [])]
        .filter(l => l.published !== false)
        .sort((a, b) => a.order_index - b.order_index),
    }))
    .filter(m => m.lessons.length > 0)
    .map((m, i) => ({ ...m, displayIndex: i + 1 }));
}

// modules: [{ id, title, order_index, lessons: [...], quizzes: [{ id }] }]
// progress: Map lesson_id -> { completed_at, updated_at }
// keepEmptyModules: for a locked course, whose lessons RLS does not return at
// all — there the module list is all there is to show.
export function buildOutline(modules, progress = new Map(), { keepEmptyModules = false } = {}) {
  const source = keepEmptyModules
    ? [...(modules || [])].sort((a, b) => a.order_index - b.order_index)
    : visibleModules(modules);
  const sortedModules = source
    .map(m => {
      const lessons = [...(m.lessons || [])]
        .filter(l => l.published !== false)
        .sort((a, b) => a.order_index - b.order_index);
      const done = lessons.filter(l => progress.get(l.id)?.completed_at).length;
      return {
        id: m.id,
        title: m.title,
        lessons,
        total: lessons.length,
        done,
        complete: lessons.length > 0 && done === lessons.length,
        quizCount: (m.quizzes || []).length,
      };
    });

  const lessons = sortedModules.flatMap(m => m.lessons);

  const done = lessons.filter(l => progress.get(l.id)?.completed_at).length;
  const pct = lessons.length ? Math.round((done / lessons.length) * 100) : 0;

  return {
    modules: sortedModules,
    lessons,
    stats: {
      modules: sortedModules.length,
      lessons: lessons.length,
      quizzes: sortedModules.reduce((sum, m) => sum + m.quizCount, 0),
    },
    done,
    pct,
    hasProgress: lessons.some(l => progress.has(l.id)),
    allDone: lessons.length > 0 && done === lessons.length,
    nextLesson: pickNextLesson(lessons, progress),
  };
}

// The lesson touched last (user_progress.updated_at) if it is unfinished;
// otherwise the first unfinished lesson after it, wrapping to the start.
// No progress at all → the first lesson.
export function pickNextLesson(lessons, progress) {
  if (lessons.length === 0) return null;

  let lastIdx = -1;
  let lastAt = '';
  lessons.forEach((l, i) => {
    const at = progress.get(l.id)?.updated_at || '';
    if (at && at > lastAt) { lastAt = at; lastIdx = i; }
  });
  if (lastIdx === -1) return lessons[0];

  const isDone = (l) => !!progress.get(l.id)?.completed_at;
  if (!isDone(lessons[lastIdx])) return lessons[lastIdx];

  for (let k = 1; k <= lessons.length; k++) {
    const l = lessons[(lastIdx + k) % lessons.length];
    if (!isDone(l)) return l;
  }
  return lessons[0];
}

// Hungarian names are written family name first, so the given name is the
// last word. Returns null when there is nothing usable.
export function givenName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : null;
}

// A szintenkénti leckeszám a course_outline soraiból (a lecke required_level-je a modul szintje):
// { free, tag (a FREE-vel együtt), osszes }. Tananyag 1. kör: a kurzusoldalon nem beégetve.
export function leckeszamSzintenkent(modules) {
  const lessons = (modules || []).flatMap(m => m.lessons || []).filter(l => l.published !== false);
  const free = lessons.filter(l => l.required_level === 'free').length;
  return { free, tag: free + lessons.filter(l => l.required_level === 'tag').length, osszes: lessons.length };
}
